import { useIntl } from "react-intl";
import { DataTableStringColumn } from "~components/DataTable/components/DataTableStringColumn/DataTableStringColumn.tsx";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import type { DataTableRow } from "~components/DataTable/types/dataTableRow.ts";
import { DivisionTableColumns } from "~pages/Divisions/DivisionListPage/enums/divisionTableColumns.ts";
import type { DivisionListItem } from "~pages/Divisions/DivisionListPage/types/divisionListItem.ts";
import { useDivisionService } from "~pages/Divisions/hooks/useDivisionService.ts";
import type { UpdateDivisionModel } from "~pages/Divisions/types/updateDivisionModel.ts";

export const useGetDivisionTableColumns = (massEditMode: boolean): Array<DataTableColumn<DivisionListItem>> => {
  const { formatMessage } = useIntl();
  const { updateDivision } = useDivisionService();

  const updateDivisionField = async (row: DivisionListItem & DataTableRow, updateData: Omit<UpdateDivisionModel, "divisionId">) => {
    row.disabled = true;
    try {
      await updateDivision(row.divisionId, updateData);
    } finally {
      row.disabled = false;
    }
  };

  return [
    {
      key: DivisionTableColumns.DivisionId,
      title: formatMessage({ id: "division.id" }),
      dataIndex: "divisionId",
      copyable: true,
      width: 320,
    },
    {
      key: DivisionTableColumns.NameLocal,
      title: formatMessage({ id: "name.local.full" }),
      dataIndex: "nameLocal",
      width: 240,
      ellipsis: { showTitle: true },
      sorter: true,
      render: (value: string, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          required
          onSave={(nameLocal) => updateDivisionField(row, { nameLocal })}
        />
      ),
    },
    {
      key: DivisionTableColumns.NameLatin,
      title: formatMessage({ id: "name.latin.full" }),
      dataIndex: "nameLatin",
      width: 240,
      ellipsis: { showTitle: true },
      sorter: true,
      render: (value: string | null, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(nameLatin) => updateDivisionField(row, { nameLatin: nameLatin || null })}
        />
      ),
    },
    {
      key: DivisionTableColumns.AbbreviationLocal,
      title: formatMessage({ id: "name.local.abbreviation" }),
      dataIndex: "abbreviationLocal",
      width: 160,
      render: (value: string | null, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(abbreviationLocal) => updateDivisionField(row, { abbreviationLocal: abbreviationLocal || null })}
        />
      ),
    },
    {
      key: DivisionTableColumns.AbbreviationLatin,
      title: formatMessage({ id: "name.latin.abbreviation" }),
      dataIndex: "abbreviationLatin",
      width: 160,
      render: (value: string | null, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(abbreviationLatin) => updateDivisionField(row, { abbreviationLatin: abbreviationLatin || null })}
        />
      ),
    },
    {
      key: DivisionTableColumns.ConferenceId,
      title: formatMessage({ id: "divisions.table.column.conferenceId" }),
      dataIndex: "conferenceId",
      copyable: true,
      width: 320,
    },
    {
      key: DivisionTableColumns.Conference,
      title: formatMessage({ id: "conference" }),
      dataIndex: "conference",
      width: 240,
      ellipsis: { showTitle: true },
    },
    {
      key: DivisionTableColumns.ExternalId,
      title: formatMessage({ id: "externalId" }),
      dataIndex: "externalId",
      copyable: true,
      width: 220,
    },
  ];
};
